
import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import AdminBrandId from "@/mixins/adminBrandId";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { DefaultFilters } from "@/data/filters/tickets";
import { DefaultSorters } from "@/data/sorters/tickets";
import { UUID, ConfirmModalModes } from "@/data/enums";

// Types
import {
  ISupportDepartmentsProvider,
  ISupportDepartmentsProviderMethods
} from "@/models/providers/supportDepartments";
import { ISorter, IFilter } from "@/models/tables";
import { ITicketDepartment } from "@/models/tickets";

@Component
export default class SupportDepartmentsProvider extends Mixins(
  FilteringSortingTableStore,
  AdminBrandId
) {
  // Props

  @Prop({ default: `SDL1` /** [S]upport [D]epartments [L]ist 1 */ })
  public declare id: string;

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => DefaultFilters() })
  public availableFilters!: IFilter[];

  @Prop({ default: () => DefaultSorters() })
  public availableSorters!: ISorter[];

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  @Prop({ default: 12 })
  public declare limit: number;

  // Data
  public isProcessing = false;
  public isReloading = false;

  public dataModule = DataModules.SUPPORT_DEPARTMENTS;
  public filters = this.availableFilters;
  public sorters = this.availableSorters;

  // Life-cycle Methods

  public created() {
    if (!this.contentIsAvailable) return;

    this.apiProps = {
      ...this.manualFilters,
      brand_id: UUID.ORG
    };

    this.loadData();

    this.$bus.$on("RefreshSupportDepartments", this.reloadData);
  }

  public beforeDestroy() {
    this.$bus.$off("RefreshSupportDepartments", this.reloadData);
  }

  // Methods

  public async loadData(ignoreStored = false) {
    try {
      await this.getData({ ignoreStored });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isReloading = false;
    }
  }

  public async reloadData() {
    this.isReloading = true;
    this.loadData(true);
  }

  public async deleteSupportDepartment(
    supportDepartmentId: ITicketDepartment["id"]
  ) {
    if (!this.canDelete) return;

    const modal = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        parent: this,
        props: {
          mode: ConfirmModalModes.PIN,
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.support.confirm_delete_dept")
        },
        canCancel: [],
        events: {
          confirmed: async () => {
            try {
              this.isProcessing = true;

              await this.delete(supportDepartmentId);
              await this.reloadData();

              this.$store.dispatch("toast/show", {
                message: this.$t("_sentence.support.dept_successfully_removed")
              });
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            } finally {
              this.isProcessing = false;
              modal.close();
            }
          }
        }
      }
    });
  }

  public delete(supportDepartmentId: ITicketDepartment["id"]) {
    if (!this.canDelete) return;

    return this.$store.dispatch(
      `data/${DataModules.SUPPORT_DEPARTMENTS}/remove`,
      {
        id: supportDepartmentId
      }
    );
  }

  public update(department) {
    if (!this.canUpdate) return;

    this.$store.dispatch(
      `data/${DataModules.SUPPORT_DEPARTMENTS}/openManageModal`,
      {
        props: {
          department
        },
        events: {
          success: this.reloadData
        }
      }
    );
  }

  public openManageSupportDepartmentModal(
    supportDepartmentId: ITicketDepartment["id"]
  ) {
    return this.$store.dispatch(
      `data/${DataModules.SUPPORT_DEPARTMENTS}/openManageSupportDepartmentModal`,
      {
        props: {
          supportDepartmentId
        },
        events: {
          success: this.reloadData
        }
      }
    );
  }

  public async switchAssignment({
    isAssigned,
    supportDepartmentId
  }: {
    isAssigned: boolean;
    supportDepartmentId: ITicketDepartment["id"];
  }) {
    if (!this.canUpdate) return;

    try {
      this.isProcessing = true;

      if (isAssigned)
        await this.assignBrand({
          ticket_department_id: supportDepartmentId,
          brand_id: this.brandId
        });
      else await this.unassignBrand(supportDepartmentId);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      await this.reloadData();
      this.isProcessing = false;
    }
  }

  public assignBrand(data) {
    return this.$store.dispatch(
      `data/${DataModules.SUPPORT_DEPARTMENTS_BRAND}/assign`,
      { data }
    );
  }

  public unassignBrand(supportDepartmentId: ITicketDepartment["id"]) {
    return this.$store.dispatch(
      `data/${DataModules.SUPPORT_DEPARTMENTS_BRAND}/unassign`,
      { supportDepartmentId, brandId: this.brandId }
    );
  }

  public updateBrandAssignment(data) {
    return this.$store.dispatch(
      `data/${DataModules.SUPPORT_DEPARTMENTS_BRAND}/update`,
      { data }
    );
  }

  // Guarding

  public get canCreate() {
    return this.$userCan("create_tickets_departments");
  }

  public get canUpdate() {
    return this.$userCan("update_tickets_departments");
  }

  public get canDelete() {
    return this.$userCan("delete_tickets_departments");
  }

  public guardFunctionalities = ["list_tickets_departments"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("supportDepartmentsProvider")
  public providerData(): ISupportDepartmentsProvider {
    return {
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      availableFilters: this.filters,
      availableSorters: this.sorters,
      canCreate: this.canCreate,
      canDelete: this.canDelete,
      canUpdate: this.canUpdate,
      constQueryFilters: this.constQueryFilters,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      guardFunctionalities: this.guardFunctionalities,
      hasDepartments: !!this.table.data.length,
      isLoading: this.table.loading,
      isOrgMode: this.isOrgMode,
      isProcessing: this.isProcessing,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      supportDepartments: this.table.data as ITicketDepartment[],
      table: this.table,
      total: this.table.total
    };
  }

  @Provide("supportDepartmentsProviderMethods")
  public get providerMethods(): ISupportDepartmentsProviderMethods {
    return {
      deleteSupportDepartment: this.deleteSupportDepartment,
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      openManageSupportDepartmentModal: this.openManageSupportDepartmentModal,
      reloadData: this.reloadData,
      switchAssignment: this.switchAssignment
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading },
        on: { click: this.reloadData }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $supportDepartmentsData: this.providerData(),
        $supportDepartmentsMethods: this.providerMethods
      })
    );
  }
}
